// Patch notes: one release at a time, picked from the list of every release.
import type { PatchNotes } from '../src/api.js';
import { Page } from './page.js';
import { Session } from './session.js';
import { type Html, Ui } from './ui.js';

const { api, h, setHtml } = Ui;

type FighterChange = PatchNotes['fighters'][number];
type StatChange = FighterChange['stats'][number];

const CHANGE_LABEL: Record<FighterChange['change'], string> = {
  buff: 'Buff',
  nerf: 'Nerf',
  mixed: 'Mixed',
  rework: 'Rework',
};

const formatDay = (date: string): string =>
  new Date(`${date}T12:00:00`).toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
const formatShortDay = (date: string): string =>
  new Date(`${date}T12:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' });

const statValue = ({ stat }: StatChange, n: number): string => (stat === 'crit' ? `${Math.round(n * 100)}%` : `${n}`);

const statHtml = (s: StatChange): Html =>
  h`<span class="patch-stat"><span class="key">${s.stat.toUpperCase()}</span><s>${statValue(s, s.from)}</s>
    <b class="${s.to > s.from ? 'up' : 'down'}">${statValue(s, s.to)}</b></span>`;

const fighterHtml = (c: FighterChange): Html => {
  const w = Session.fighter(c.fighterId);
  return h`<article class="patch-fighter" data-class="${w?.class ?? ''}">
    <header>
      ${w ? h`<img src="/art/${w.id}/idle" alt="">` : ''}
      <div class="grow"><b>${w?.name ?? c.fighterId}</b><div class="class-tag">${w?.class ?? ''}</div></div>
      <span class="patch-change ${c.change}">${CHANGE_LABEL[c.change]}</span>
    </header>
    <div class="patch-fighter-body">
      ${c.stats.length ? h`<div class="patch-stats">${c.stats.map(statHtml)}</div>` : ''}
      ${c.skills.map(
        s => h`<div class="patch-skill"><b>${s.name}</b>${s.before ? h`<s>${s.before}</s>` : h` <span class="trigger">New</span>`}
          <div>${Ui.skillText(s.after)}</div></div>`,
      )}
    </div>
  </article>`;
};

const releaseHtml = (r: PatchNotes, latest: boolean): Html =>
  h`<section class="panel stack patch-head">
      <div class="muted">${latest ? h`<span class="patch-latest">Latest</span> ` : ''}${formatDay(r.date)}</div>
      <h2>${r.title}</h2>
      <p>${r.summary}</p>
    </section>
    ${
      r.notes.length
        ? h`<section class="stack"><h3>New</h3>${r.notes.map(
            n => h`<div class="panel patch-note"><b>${n.title}</b><p class="muted">${n.text}</p></div>`,
          )}</section>`
        : ''
    }
    ${
      r.fighters.length
        ? h`<section class="stack"><h3>Fighter changes</h3>
            <div class="panel patch-fighters">${r.fighters.map(fighterHtml)}</div></section>`
        : ''
    }`;

const listHtml = (releases: PatchNotes[], shown: PatchNotes): Html =>
  h`<aside class="patch-list" aria-label="Patches">${releases.map(
    r => h`<a href="#/patches?id=${r.id}" ${r.id === shown.id ? h`aria-current="page"` : ''}>
      <span class="muted sub">${formatShortDay(r.date)}</span><b>${r.title}</b></a>`,
  )}</aside>`;

const view = async (): Promise<void> => {
  const releases = await api<PatchNotes[]>('/api/patches');
  const picked = new URLSearchParams(location.hash.split('?')[1] ?? '').get('id');
  const [latest] = releases;
  const shown = releases.find(r => r.id === picked) ?? latest;
  setHtml(
    Page.view,
    shown
      ? h`<div class="patches">${releases.length > 1 ? listHtml(releases, shown) : ''}<div class="stack grow">${releaseHtml(shown, shown === latest)}</div></div>`
      : h`<h2>Patch notes</h2><div class="panel"><p class="muted">No patches yet.</p></div>`,
  );
};

export const Patches = { view };
